import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import {
  emptyLoreFilters,
  loreCanonStatuses,
  loreCanonStatusLabels,
  loreEntryTypes,
  loreEntryTypeLabels,
  type LoreCanonStatus,
  type LoreEntryFilters,
  type LoreEntryType,
} from "@/features/lore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTranslation } from "react-i18next";

type LoreFilterBarProps = {
  filters: LoreEntryFilters;
  onClearFilters: () => void;
  onSearchTextChange: (searchText: string) => void;
  onStatusFilterChange: (statusFilter: LoreCanonStatus | null) => void;
  onTypeFilterChange: (typeFilter: LoreEntryType | null) => void;
};

const LORE_FILTER_EXPANDED_STORAGE_KEY = "lore:filters-expanded";

function readExpandedState() {
  if (typeof window === "undefined") {
    return true;
  }

  const storedValue = window.localStorage.getItem(LORE_FILTER_EXPANDED_STORAGE_KEY);
  return storedValue === null ? true : storedValue === "true";
}

export function LoreFilterBar({
  filters,
  onClearFilters,
  onSearchTextChange,
  onStatusFilterChange,
  onTypeFilterChange,
}: LoreFilterBarProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(readExpandedState);
  const hasFilters =
    filters.searchText.trim().length > 0
    || filters.typeFilter !== emptyLoreFilters.typeFilter
    || filters.statusFilter !== emptyLoreFilters.statusFilter;

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(LORE_FILTER_EXPANDED_STORAGE_KEY, String(expanded));
  }, [expanded]);

  return (
    <div className="space-y-4 border-t border-ink/10 pt-6">
      <button
        className="flex w-full items-center justify-between gap-3 text-left"
        onClick={() => setExpanded((current) => !current)}
        type="button"
      >
        <p className="text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
          {t("lore.filters.title")}
        </p>
        {expanded ? <ChevronUp className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
      </button>

      {expanded ? (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label={t("lore.filters.searchLabel")}
              className="pl-11"
              onChange={(event) => onSearchTextChange(event.target.value)}
              placeholder={t("lore.filters.searchPlaceholder")}
              value={filters.searchText}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Select
              onValueChange={(value) =>
                onTypeFilterChange(value === "all" ? null : (value as LoreEntryType))}
              value={filters.typeFilter ?? "all"}
            >
              <SelectTrigger aria-label={t("lore.filters.typeLabel")}>
                <SelectValue placeholder={t("lore.filters.allTypes")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("lore.filters.allTypes")}</SelectItem>
                {loreEntryTypes.map((type) => (
                  <SelectItem key={type} value={type}>
                    {t(loreEntryTypeLabels[type])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              onValueChange={(value) =>
                onStatusFilterChange(value === "all" ? null : (value as LoreCanonStatus))}
              value={filters.statusFilter ?? "all"}
            >
              <SelectTrigger aria-label={t("lore.filters.statusLabel")}>
                <SelectValue placeholder={t("lore.filters.allStatuses")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t("lore.filters.allStatuses")}</SelectItem>
                {loreCanonStatuses.map((status) => (
                  <SelectItem key={status} value={status}>
                    {t(loreCanonStatusLabels[status])}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            disabled={!hasFilters}
            onClick={onClearFilters}
            type="button"
            variant="ghost"
            className="h-auto rounded-full px-0 py-0 text-xs font-semibold uppercase tracking-[0.16em] text-clay hover:bg-transparent hover:text-foreground"
          >
            {t("lore.filters.clear")}
          </Button>
        </>
      ) : null}
    </div>
  );
}
